import fs from 'node:fs/promises';
import crypto from 'node:crypto';

import { enhanceOpenApiWithSourceInfo } from './services';
import { transformOpenapiPaths } from './utils';

import type { Request, Response } from 'express';
import type { OpenapiCache } from './types';
import type { MiddlewareContext } from '../types';

/**
 * Create OpenAPI request handler with caching
 */
export function createOpenapiHandler(
  openapiFilePath: string,
  enableEnhancement: boolean,
  serverDir?: string,
) {
  // In-memory cache
  let cache: OpenapiCache | null = null;

  return async (_req: Request, res: Response, context: MiddlewareContext) => {
    try {
      // Read OpenAPI file
      const fileBuffer = await fs.readFile(openapiFilePath, 'utf-8');

      // Calculate file hash for cache invalidation
      const currentHash = crypto.createHash('md5').update(fileBuffer).digest('hex');

      // Use cache if file content hasn't changed
      if (cache && cache.fileHash === currentHash) {
        return res.json(cache.data);
      }

      // Parse OpenAPI
      let payload = JSON.parse(fileBuffer);

      // Enhance with x-source if enabled
      if (enableEnhancement && context.isDev) {
        const { openapi: enhancedPayload, stats } = await enhanceOpenApiWithSourceInfo({
          openapiData: payload,
          writeFile: false, // Don't write to file, keep in memory
          serverDir: serverDir || context.rootDir, // Use provided serverDir or fallback to context.rootDir
        });
        payload = enhancedPayload;

        // Log performance
        console.log(`[OpenAPI] Enhanced in ${stats.duration}ms (${stats.endpointsEnhanced} endpoints)`);
      }

      // Transform paths: remove basePath prefix from all API paths
      const result = transformOpenapiPaths(payload, context.basePath);

      // Update cache
      cache = {
        data: result,
        fileHash: currentHash,
      };

      res.json(result);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      res.status(500).json({
        error: 'Failed to load OpenAPI spec',
        message,
      });
    }
  };
}
